<template>
    <viewer-tab ref="tab" title="Gear Modeler" v-loading="loading" element-loading-text="Saving"
                :typename="$options.typename" :lib-obj="libObject"
                :url-help="$urls.HELP_MODEL_GEAR" :accept="handleAccept" :click-x="clickX"
                @opened="handleTabOpened" @active="handleTabActive">
        <div class="pane-grid-1-2">
            <div class="pane-1">
                <!--INFO-->
                <ModelerInfo ref="formInfo" :form-info="formInfo"/>

                <!--GEOMETRY-->
                <div class="section-heading">
                    <h4>Geometry</h4>
                    <hr>
                    <el-switch
                            class="heading-switch"
                            v-model="cutWorkflow"
                            inactive-text="Tooth Form"
                            active-text="Tooling"
                            inactive-color="var(--color-secondary)"
                            active-color="var(--color-secondary)"
                            @change="changeCutWorkflow">
                    </el-switch>
                </div>
                <FormGearDetailTooth
                        ref="formTooth"
                        :form="formTooth"
                        :form-generating-rack="formGeneratingRack"
                        :form-generating-pinion="formGeneratingPinion"
                        :internal="internal"
                        :cut-workflow="cutWorkflow"
                        :cut-type-options="cutTypeOptions"
                        :is-generating-rack="isGeneratingRack"
                        :is-generating-pinion="isGeneratingPinion"
                        :label-width="$options.labelWidth"
                        :units="$options.units"
                        :update-geom-fields="updateGeomFields"
                        @change-cut-type="changeCutType"
                        @change-tooth-geom="changeToothGeom"
                        @change-tool-geom="changeToolGeom"
                />

                <!--FINISHING-->
                <div class="section-heading">
                    <h4>Finishing</h4>
                    <hr>
                </div>
                <el-form
                        ref="formFinish"
                        class="pb-cushion"
                        :model="formFinish"
                        :rules="rulesFinish"
                        size="mini"
                        :show-message="false"
                        hide-required-asterisk>
                    <div class="col-form-items">
                        <el-form-item class="form-item" prop="toothThicknessNormalFinish" :label-width="$options.labelWidth.col1">
                            <span slot="label">
                                <el-tooltip content="Normal tooth thickness resulting from the finishing process."
                                            placement="top-start" :tabindex="-1" :open-delay="$defaults.tooltipDelay">
                                    <span>Finished tt<sub>n</sub><small><i :class="$icons.chatBox"/></small></span>
                                </el-tooltip>
                            </span>
                            <el-input v-model="formFinish.toothThicknessNormalFinish" class="form-field"
                                      @change="changeFinish" :disabled="cutWorkflow || internal">
                                <template slot="suffix">mm</template>
                            </el-input>
                        </el-form-item>
                        <el-form-item class="form-item" prop="finishStockNormal" :label-width="$options.labelWidth.col2">
                            <span slot="label">Finishing stock<sub>n</sub></span>
                            <el-input v-model="formFinish.finishStockNormal" class="form-field"
                                      @change="changeFinish" :disabled="!cutWorkflow || internal">
                                <template slot="suffix">mm</template>
                            </el-input>
                        </el-form-item>
                    </div>
                    <div class="col-form-items">
                        <el-form-item class="form-item" prop="flankRoughnessRa" :label-width="$options.labelWidth.col1">
                            <span slot="label">Flank roughness<sub>Ra</sub></span>
                            <el-input v-model="formFinish.flankRoughnessRa" class="form-field">
                                <template slot="suffix">mm</template>
                            </el-input>
                        </el-form-item>
                        <el-form-item class="form-item" prop="rootRoughnessRa" :label-width="$options.labelWidth.col2">
                            <span slot="label">Root roughness<sub>Ra</sub></span>
                            <el-input v-model="formFinish.rootRoughnessRa" class="form-field">
                                <template slot="suffix">mm</template>
                            </el-input>
                        </el-form-item>
                    </div>
                </el-form>
            </div>
            <div class="pane-2 pane-2-pixi" v-loading="loadingGraphics" element-loading-text="Cutting gear">
                <canvas v-show="!showGeneratingDiagram" ref="pixi" class="pixi"/>
                <generating-diagram
                        v-show="showGeneratingDiagram"
                        ref="generatingDiagram"
                        :tooth="this.formTooth"
                        :generating-rack="this.formGeneratingRack"
                        :generating-pinion="this.formGeneratingPinion"
                        :computed-pinion-cutter="this.computedPinionCutter"
                        :finished-root="this.finishedRoot"
                        :internal="this.internal"
                        :units="$options.units"/>
                <small id="pane-2-note">Transverse plane</small>
                <div id="pane-2-btns">
                    <el-popover placement="bottom-start" width="150" :visible-arrow="false" trigger="click">
                        <el-button slot="reference" class="btn-two" type="text" icon="el-icon-s-operation" :disabled="!legend"/>
                        <table id="table-legend">
                            <tr v-for="item in legend" :key="item.label">
                                <td :style="{backgroundColor: item.color}"/>
                                <td>{{ item.label }}</td>
                            </tr>
                        </table>
                    </el-popover>
                    <el-tooltip content="Toggle cutter generation diagram" placement="bottom" :open-delay="$defaults.tooltipDelay">
                        <el-button class="btn-two" type="text" icon="el-icon-scissors" :disabled="!legend" @click="toggleGeneratingDiagram"/>
                    </el-tooltip>
                </div>
                <el-slider v-show="!showGeneratingDiagram" id="slider-rotation"
                           v-model="rotater.value"
                           :show-tooltip="false"
                           :min="rotater.min"
                           :max="rotater.max"
                           :step="(rotater.max - rotater.min) / rotater.steps">
                </el-slider>
            </div>
            <div class="pane-3 pane-3-tables">
                <!--PLACEHOLDER {UX} table of gear warnings and errors; see ReportModel-->
                <modeler-tables :tables="tables"/>
            </div>
        </div>
    </viewer-tab>
</template>

<script>
  import * as PIXI from "../../config/pixi";
  import ViewerTab from "../viewer/ViewerTab";
  import ModelerInfo from "../_forms/ModelerInfo";
  import ModelerTables from "../_tables/ModelerTables";
  import FormGearDetailTooth from "../_gears/FormGearDetailTooth";
  import GeneratingDiagram from "../_gears/GeneratingDiagram";
  import math from "../../assets/js/math";
  import gear from "../../assets/js/gear";
  import pixi from "../../assets/js/pixi/parts";
  import { copyDeepJSON, assignProps, copyDeep, ResizeListener } from "../../assets/js/utils";
  import { hobMaxTipRadius } from "../../assets/js/chart/tool";
  import { rules } from "../../assets/js/validators";
  import { EventBus } from "../../event_bus";
  import { apolloQuery } from "../../config/apollo";
  import requests from "../../requests";
  import gql from 'graphql-tag';

  export default {
    name: "ModelGearMacro",
    components: {GeneratingDiagram, ViewerTab, ModelerInfo, ModelerTables, FormGearDetailTooth},
    props: {
      clickX: Function,
      openId: String,
      lib: Boolean,  // flag lib or part object
      name: String,  // unique tab name
      internal: {
        type: Boolean,
        default: false
      },
    },

    // STATIC DATA
    typename: requests.gear.TYPENAME.GEAR_DETAIL,
    labelWidth: {
      col1: '135px',
      col2: '135px',
    },
    units: {
      angle: 'deg',
    },
    drawTeeth: 7,
    rotationOffset: -Math.PI / 2,

    data() {
      // CUSTOM VALIDATORS
      const validateTTNormalFinish = (rule, value, callback) => {
        const toothThicknessNormalFinish = Number.parseFloat(this.formFinish.toothThicknessNormalFinish);
        const toothThicknessNormal = Number.parseFloat(this.formTooth.toothThicknessNormal);
        if (toothThicknessNormalFinish > toothThicknessNormal) {
          callback(new Error('Finished tooth thickness is larger than pre-finished.'));
        } else {
          callback();  // valid
        }
      };
      return {
        // APOLLO
        loading: false,
        libObject: null,

        // WORKFLOW
        cutWorkflow: true,

        // FORMS NON-GEOM
        formInfo: {
          name: '',
          number: '',
          description: '',
        },

        // FORMS GEOM
        formTooth: {
          numberOfTeeth: this.internal ? '50' : '20',
          moduleNormal: '2',
          pressureAngleNormal: '20',
          helixAngle: '0',
          rootDiameter: '',
          tipDiameter: '',
          profileShiftCoefficient: '0',
          toothThicknessNormal: '',
          rimDiameter: this.internal ? '120' : '0',
          workPieceDiameter: this.internal ? '95' : '45',
          facewidth: '10',
          cutType: this.internal ? this.$requests.gear.CutType.GEN_PINION : this.$requests.gear.CutType.GEN_RACK,
        },
        formGeneratingRack: {
          modifiedLead: false,
          pressureAngleNormal: '20',
          tipRadiusCoefficient: '0.25',
          tipRadiusMax: false,
          protuberanceSize: '0',
          protuberanceAngle: '0',
          addendumCoefficient: '1.25',
          dedendumCoefficient: '1.0',
          // hidden fields
          profileShiftCoefficient: '',
          moduleNormal: '2',
          helixAngle: '0',
        },
        formGeneratingPinion: {
          modifiedLead: false,
          numberOfTeeth: '31',
          pressureAngleNormal: '20',
          tipRadiusCoefficient: '0.25',
          tipRadiusMax: false,
          protuberanceSize: '0',
          addendumCoefficient: '1.25',
          dedendumCoefficient: '1.0',
          profileShiftCoefficient: '0',
          // hidden fields
          moduleNormal: '2',
          helixAngle: '0',
          rootDiameter: '',
          tipDiameter: '',
          toothThicknessNormal: '',
          workingPressureAngleTransverse: '',
          workingCenterDistance: '',
          referenceCenterDistance: '',
        },
        formFinish: {
          toothThicknessNormalFinish: '',
          finishStockNormal: '0',
          flankRoughnessRa: '',
          rootRoughnessRa: '',
        },
        rulesFinish: {
          // use cases exist for negative reference tooth thickness
          toothThicknessNormalFinish: [].concat(rules.requiredNumber, {validator: validateTTNormalFinish, trigger: ['blur']}),
          finishStockNormal: rules.requiredZeroPositiveNumber,
          flankRoughnessRa: rules.optionalPositiveNumber,
          rootRoughnessRa: rules.optionalPositiveNumber,
        },

        // TABLE PARAMS
        tableGeom: [],
        docSharing: {},  // owner, shared, lock
        docSigned: {},  // lastModifiedOn, createdOn, etc.

        // GRAPHICS
        pixi: null,
        pixiGear: null,
        pixiCircles: null,
        rotater: {
          value: 0,
          min: 0,
          max: 1,
          steps: 100
        },
        legend: null,
        loadingGraphics: false,
        resizeListener: null,
        renderPixi: true,

        // GENERATION DIAGRAMS
        finishedRoot: null,
        computedPinionCutter: null,
        showGeneratingDiagram: false,
        renderGeneratingDiagram: false,
      }
    },
    computed: {
      isEditing() {
        // checks if editing an existing object
        return typeof this.openId !== 'undefined';
      },
      tableFile() {
        return this.$handlers.lib.tableFile({
          ...this.formInfo,
          ...this.docSharing,
          ...this.docSigned,
          owner: this.$store.state.session.user.Id,
        });
      },
      tableTooling() {
        switch (this.formTooth.cutType) {
          case this.$requests.gear.CutType.GEN_RACK:
            return this.$handlers.gear.tableGeneratingRack(this.formGeneratingRack, this.$options.units);
          case this.$requests.gear.CutType.GEN_PINION:
            return this.$handlers.gear.tableGeneratingPinion(this.formGeneratingPinion, this.$options.units);
        }
      },
      tables() {
        return [{
          table: this.tableGeom,
          title: 'Geometry',
        }, {
          table: this.tableTooling,
          title: 'Tooling',
        }, {
          table: this.tableFile,
          title: 'File',
        }]
      },
      // CUTTING TYPE
      cutTypeOptions() {
        if (this.internal) {
          return [this.$requests.gear.CutType.GEN_PINION]
        } else {
          return [this.$requests.gear.CutType.GEN_RACK, this.$requests.gear.CutType.GEN_PINION];
        }
      },
      isGeneratingRack() {
        return this.formTooth.cutType === this.$requests.gear.CutType.GEN_RACK;
      },
      isGeneratingPinion() {
        return this.formTooth.cutType === this.$requests.gear.CutType.GEN_PINION;
      },
      // OTHER
      rotaterStepSize() {
        return (this.rotater.max - this.rotater.min) / this.rotater.steps;
      },
    },
    apollo: {
      // If editing existing item, start watch query to map form data.
      // Watch query used to reactively update to changes from sharing dialog.
      // Sacrificing performance for clarity and robustness by not including ${requests.eng.finishedToothQueryStr}.
      // - Mainly doing this because it gets more complicated with pinion generation needing to query more data.
      libObject: {
        skip() { return !this.isEditing },
        fetchPolicy: 'cache-and-network',
        variables() { return { Id: this.openId } },
        query: gql`query getGearDetail ($Id: ID!) {
            libObject: gearDetail (Id: $Id) {
              ${requests.lib.GearDetail}
            }
          }`,
        result({ data }) {
          // deep copy to avoid mutation of Apollo cache data
          let libObj = copyDeep(data.libObject);
          this.$handlers.gear.convertGearGeom(libObj, undefined, this.$options.units);

          // DEV ONLY
          // this.$message.warning(String(gearDetail.geom.pressureAngleNormal));

          // map lib object to form data
          this.mapLib2Forms(libObj);

          // define graphics
          if (!this.pixi) this.newPixi();
          this.updatePixi();
        },
      },
    },

    methods: {
      // region APOLLO
      // APOLLO --> FORMS
      mapGeom2Forms(geom) {
        // Map gear geom schema to forms.
        // Required because forms do not exactly use the persisted data structure.

        // convert values for internal gearing
        if (this.internal) this.$handlers.gear.gearGeomInternalFlip({geom});

        // map gear type and workflow
        this.cutWorkflow = geom.cutWorkflow;

        // map reference tooth data
        assignProps(this.formTooth, geom);

        // map finish data
        assignProps(this.formFinish, geom);

        // map tooling data
        switch (geom.cutType) {
          case this.$requests.gear.CutType.GEN_RACK: {
            assignProps(this.formGeneratingRack, geom.cutGeneratingRack);

            // initialize modified lead; required since non-persisted
            if (this.formGeneratingRack.pressureAngleNormal !== this.formTooth.pressureAngleNormal) {
              this.formGeneratingRack.modifiedLead = true;
            }
            break;
          }
          case this.$requests.gear.CutType.GEN_PINION: {
            assignProps(this.formGeneratingPinion, geom.cutGeneratingPinion);

            // initialize modified lead; required since non-persisted
            if (this.formGeneratingPinion.pressureAngleNormal !== this.formTooth.pressureAngleNormal) {
              this.formGeneratingPinion.modifiedLead = true;
            }
            break;
          }
        }

        // calculate non-persisted tool data
        this.updateToolFields(false);

        // map optionally persisted tooling data
        switch (geom.cutType) {
          case this.$requests.gear.CutType.GEN_RACK: {
            const dedendumCoefficient = geom.cutGeneratingRack.dedendumCoefficient;
            if (dedendumCoefficient) this.formGeneratingRack.dedendumCoefficient = dedendumCoefficient;
            break;
          }
          case this.$requests.gear.CutType.GEN_PINION: {
            const dedendumCoefficient = geom.cutGeneratingPinion.dedendumCoefficient;
            if (dedendumCoefficient) this.formGeneratingPinion.dedendumCoefficient = dedendumCoefficient;
            break;
          }
        }
      },
      async mapLib2Forms(gear) {
        // args: GearDetail type

        let geom;

        // set info, sharing, and signed data
        [this.formInfo, this.docSharing, this.docSigned, geom] = this.$handlers.lib.libObjectForms(gear);

        // DEV ONLY
        // console.log('GEOM', geom);
        // console.log('REST', rest);

        // map geom form
        this.mapGeom2Forms(geom);
      },

      // APOLLO <-- FORMS
      mapForms2Geom(convert=false) {
        // Map forms to gear geom schema.
        // Required because forms do not exactly use the persisted data structure.

        // initialize
        let geom = copyDeepJSON(this.$requests.gear.GearDetailGeomInput);
        let cutGeneratingRack, cutGeneratingPinion;

        // map tooling data
        switch (this.formTooth.cutType) {
          case this.$requests.gear.CutType.GEN_RACK: {
            cutGeneratingRack = {...this.formGeneratingRack};
            cutGeneratingPinion = null;
            break;
          }
          case this.$requests.gear.CutType.GEN_PINION: {
            cutGeneratingRack = null;
            cutGeneratingPinion = {...this.formGeneratingPinion};
            break;
          }
        }

        // assign form data to geom
        assignProps(geom, {
          cutWorkflow: this.cutWorkflow,
          cutGeneratingRack: cutGeneratingRack,
          cutGeneratingPinion: cutGeneratingPinion,
          ...this.formTooth,
          ...this.formFinish,
        });

        // convert units
        if (convert) this.$handlers.gear.convertGearGeom({geom}, this.$options.units);

        // convert values for internal gearing
        if (this.internal) this.$handlers.gear.gearGeomInternalToAPI({geom});

        return geom;
      },
      mapForms2PinionGeom() {
        // Map forms to pinion cutter geom schema.

        // initialize
        let geom = copyDeepJSON(this.$requests.tool.PinionCutterGeomInput);

        // assign form data to geom
        assignProps(geom, this.formGeneratingPinion);

        // convert units for API
        this.$handlers.tool.convertPinionCutterGeom({geom}, this.$options.units, undefined);

        return geom;
      },

      // APOLLO QUERIES
      async queryFinishedRoot(geom) {
        // API query of gear root coordinates.
        // arg: GearDetail geom field, with angular units in radians.
        // PLACEHOLDER {server-side warnings} return warnings for invalid geometry:
        //  1. toolTipRadiusCoefficient is too large
        //  2. protuberanceBoundaryDiameter is null when the the finished TT creates a step change at root fillet

        this.loadingGraphics = true;

        // define API query
        let variables, queryStr;
        if (this.isGeneratingPinion) {
          variables = {gear: geom, tool: this.mapForms2PinionGeom()};
          queryStr = gql`query computeToothAndPinionCutter($gear: GearDetailGeomInput!, $tool: PinionCutterGeomInput!) {
            computeTooth(data: $gear) {
              ${this.$requests.eng.ToothFinishedParams}
            }
            computePinionCutter(data: $tool) {
              compute {
                ${this.$requests.eng.PinionCutterCompute}
              }
            }
          }`
        } else {
          variables = {data: geom};
          queryStr = gql`query computeToothRoot($data: GearDetailGeomInput!) {
            computeTooth(data: $data) {
              ${this.$requests.eng.ToothFinishedParams}
            }
          }`
        }

        const query = {
          errorPolicy: "ignore",
          fetchPolicy: "network-only",
          variables: variables,
          query: queryStr,
        };
        let {data: {computeTooth, computePinionCutter}} = await apolloQuery(query)
          .finally(() => this.loadingGraphics = false);

        // update pinion tip radius
        if (this.isGeneratingPinion) {
          this.computedPinionCutter = computePinionCutter.compute;
          if (computePinionCutter.compute && this.formGeneratingPinion.tipRadiusMax) {
            this.formGeneratingPinion.tipRadiusCoefficient = computePinionCutter.compute.tipRadiusCoefficient;
          }
        }

        // update geometry table
        this.updateTableGeom(computeTooth);

        return computeTooth.finishedRoot;
      },

      // APOLLO MUTATE
      forms2MutateInputLib(Id=undefined) {
        // Define GraphQL mutation input variable for creating or updating library object.
        // args: Id for use with library update mutations

        let libMutationInput = {
          ...this.formInfo,
          geom: this.mapForms2Geom(),
        };

        // DEV ONLY
        // console.log('MUTATION INPUT', libMutationInput);

        if (typeof Id === 'undefined') {
          return libMutationInput
        } else {
          return {
            Id: Id,
            ...libMutationInput,
          }
        }
      },
      async mutateCreateLib() {
        // API mutation to create new library gear.
        // Returns created gear.

        this.loading = true;
        const mutate = {
          variables: {
            data: {
              ...this.forms2MutateInputLib(),
              lock: false,
              shared: [],
            }
          },
          mutation: gql`mutation createGearDetail ($data: GearDetailInput!) {
            createGearDetail (data: $data) {
              gearDetail {
                ${this.$requests.lib.gearDetailCollectionFields}
              }
            }
          }`,
          update: (cache, { data: { createGearDetail: { gearDetail } } }) => {
            // Update cache of collection to avoid a network refetch.

            const query = {
              variables: {
                limit: this.$requests.lib.LIMIT
              },
              query: gql`query getGearsDetail ($limit: Int) {
                gearsDetail (limit: $limit) {
                  __typename
                  ${this.$requests.lib.gearDetailCollectionFields}
                }
              }`,
            };

            try {
              // Read the data from our cache for this query.
              const data = cache.readQuery(query);
              // Modify the query data object.
              data.gearsDetail.unshift(gearDetail);
              // Write modified data back to the cache.
              cache.writeQuery({ ...query, data })
            } catch (e) {
              // cache does not contain query
            }
          },
        };
        const { gearDetail } = await this.$gql.mutateConvertUnits(mutate, this.$options.units)
          .finally(() => this.loading = false);

        this.$notify.success({
          position: this.$defaults.notifyPosition,
          title: 'New Gear',
          message: gearDetail.name + ' added to library',
        });

        return gearDetail
      },
      async mutateUpdateLib(Id) {
        // API mutation to update existing library gear.
        // Returns updated gear.

        this.loading = true;

        // define query str depending on if parts will be affected
        let parts = this.$store.getters.getPartsGearDetail();
        parts = parts.filter(part => part.obj.Id === Id);
        const usedInParts = parts.length > 0;

        let queryStr;
        if (usedInParts) {
          // query fields required by both lib and parts
          queryStr = `
            ${this.$requests.lib.GearDetail}
            ${this.$requests.eng.finishedToothQueryStr}
          `;
        } else {
          // query only fields required by lib
          queryStr = this.$requests.lib.GearDetail;
        }

        const mutate = {
          variables: {
            data: this.forms2MutateInputLib(Id)
          },
          mutation: gql`mutation updateGearDetail ($data: UpdateGearDetailInput!) {
            updateGearDetail (data: $data) {
              gearDetail {
                ${queryStr}
              }
            }
          }`,
        };
        const { gearDetail } = await this.$gql.mutateConvertUnits(mutate, this.$options.units)
          .finally(() => this.loading = false);

        this.$notify.success({
          position: this.$defaults.notifyPosition,
          title: 'Updated Gear',
          message: gearDetail.name + ' saved to library',
        });

        // trigger global event to redraw any affected parts; getPart fetches updated cache data
        parts.forEach(part => EventBus.$emit('redraw-part', this.$store.getters.getPart(part.Id)));

        return gearDetail
      },
      async mutateCreatePart() {
        // API mutation to create new library gear and add as model part.

        // create library object
        const obj = await this.mutateCreateLib();

        // add part to model
        this.loading = true;
        const typ = this.$requests.model.PartType.GEAR_DETAIL;
        await this.$store.dispatch('addPart', {obj: obj, typ: typ})
          .finally(() => this.loading = false);
      },
      // endregion

      // TOOTH DATA
      changeCutType() {
        this.updateGeomFields();
        this.updatePixi();
      },
      changeCutWorkflow() {
        // DOES NOTHING
      },
      applyLeadConstraint() {
        // If standard lead is specified, then set tool pressure angle equal to tooth reference pressure angle.
        // Both rack and pinion tools are considered.

        // rack tool
        if (!this.formGeneratingRack.modifiedLead) {
          this.formGeneratingRack.pressureAngleNormal = this.formTooth.pressureAngleNormal;
        }

        // pinion tool
        if (!this.formGeneratingPinion.modifiedLead) {
          this.formGeneratingPinion.pressureAngleNormal = this.formTooth.pressureAngleNormal;
        }
      },
      updateToothFields() {
        // Update reference tooth data based on tool data.
        // This should only be called with 'tooling' workflow is being used.

        let toothData;
        switch (this.formTooth.cutType) {
          case this.$requests.gear.CutType.GEN_RACK: {
            toothData = gear.referenceToothFromRack2(this.formTooth, {
              genPressureAngleNormal: this.formGeneratingRack.pressureAngleNormal,
              genAddendumCoefficient: this.formGeneratingRack.addendumCoefficient,
              genDedendumCoefficient: this.formGeneratingRack.dedendumCoefficient,
            }, this.$options.units.angle);
            break;
          }
          case this.$requests.gear.CutType.GEN_PINION: {
            toothData = gear.referenceToothFromPinion1(this.formTooth, {
              genPressureAngleNormal: this.formGeneratingPinion.pressureAngleNormal,
              cutterNumberOfTeeth: this.formGeneratingPinion.numberOfTeeth,
              cutterProfileShiftCoefficient: this.formGeneratingPinion.profileShiftCoefficient,
              cutterAddendumCoefficient: this.formGeneratingPinion.addendumCoefficient,
              cutterDedendumCoefficient: this.formGeneratingPinion.dedendumCoefficient,
            }, this.internal, this.$options.units.angle);
            break;
          }
        }

        // update form fields of reference tooth
        this.formTooth.tipDiameter = toothData.tipDiameter;
        this.formTooth.rootDiameter = toothData.rootDiameter;
        this.formTooth.toothThicknessNormal = toothData.toothThicknessNormal;
        this.updateToothThicknessNormalFinish();
      },
      updateToolFields(updateWorkPiece=true) {
        // Update generating tool data based on reference tooth data and generating pressure angle.
        // This should only be called with 'tooth form' workflow is being used.
        // arg: updateWorkPiece=true sets the work piece diameter to the tip diameter.

        switch (this.formTooth.cutType) {
          case this.$requests.gear.CutType.GEN_RACK: {
            const genPressureAngleNormal = this.formGeneratingRack.pressureAngleNormal;
            const toolData = gear.generatingRackData(this.formTooth, genPressureAngleNormal, this.$options.units.angle);

            // update form fields of generating tool
            this.formGeneratingRack.addendumCoefficient = toolData.addendumCoefficient;
            this.formGeneratingRack.dedendumCoefficient = toolData.dedendumCoefficient;

             // update hidden fields of generating tool
            this.formGeneratingRack.profileShiftCoefficient = toolData.profileShiftCoefficient;
            this.formGeneratingRack.moduleNormal = toolData.moduleNormal;
            this.formGeneratingRack.helixAngle = toolData.helixAngle;
            break;
          }
          case this.$requests.gear.CutType.GEN_PINION: {
            const toolData = gear.generatingPinionData(this.formTooth, {
              genPressureAngleNormal: this.formGeneratingPinion.pressureAngleNormal,
              cutterNumberOfTeeth: this.formGeneratingPinion.numberOfTeeth,
              cutterProfileShiftCoefficient: this.formGeneratingPinion.profileShiftCoefficient
            }, this.internal, this.$options.units.angle);

            // update all fields of generating tool
            assignProps(this.formGeneratingPinion, toolData);
            break;
          }
        }

        // update form fields of reference tooling
        if (updateWorkPiece) this.formTooth.workPieceDiameter = this.formTooth.tipDiameter;
        this.formTooth.profileShiftCoefficient = gear.profileShiftCoefficient2({
          moduleNormal: this.formTooth.moduleNormal,
          pressureAngleNormal: math.angle2rad(this.formTooth.pressureAngleNormal, this.$options.units.angle),
          helixAngle: math.angle2rad(this.formTooth.helixAngle, this.$options.units.angle),
          toothThicknessNormal: this.formTooth.toothThicknessNormal
        });

        // UX options: 1) update finishing stock, 2) constrain finish to pre-finish
        // constrain finished tooth thickness to pre-finished
        this.formFinish.toothThicknessNormalFinish = this.formTooth.toothThicknessNormal;
        this.formFinish.finishStockNormal = 0;
      },
      updateToolHiddenFields() {
        // Update hidden generating tool data based on reference tooth data and generating pressure angle.
        // Does not modify the editable form fields of generating tool.

        switch (this.formTooth.cutType) {
          case this.$requests.gear.CutType.GEN_RACK: {
            const genPressureAngleNormal = this.formGeneratingRack.pressureAngleNormal;
            const toolData = gear.generatingRackData(this.formTooth, genPressureAngleNormal, this.$options.units.angle);

            // update hidden fields of generating tool
            this.formGeneratingRack.profileShiftCoefficient = toolData.profileShiftCoefficient;
            this.formGeneratingRack.moduleNormal = toolData.moduleNormal;
            this.formGeneratingRack.helixAngle = toolData.helixAngle;
            break;
          }
          case this.$requests.gear.CutType.GEN_PINION: {
            // notice cutterDedendumCoefficient arg to avoid topping cutter assumption
            const toolData = gear.generatingPinionData(this.formTooth, {
              genPressureAngleNormal: this.formGeneratingPinion.pressureAngleNormal,
              cutterNumberOfTeeth: this.formGeneratingPinion.numberOfTeeth,
              cutterProfileShiftCoefficient: this.formGeneratingPinion.profileShiftCoefficient,
              cutterDedendumCoefficient: this.formGeneratingPinion.dedendumCoefficient,
            }, this.internal, this.$options.units.angle);

            // update hidden fields of generating tool; first removing form fields
            delete toolData.addendumCoefficient;
            delete toolData.dedendumCoefficient;
            assignProps(this.formGeneratingPinion, toolData);
            break;
          }
        }
      },
      updateGeomFields() {
        // Try updating form fields based on the chosen workflow, tooth or tooling.
        // If tooth workflow, update tooling data based on reference tooth.
        // If tool workflow, update reference tooth data based on tooling.

        // apply lead constraint
        this.applyLeadConstraint();

        // update geometry fields
        if (!this.cutWorkflow) {
          this.updateToolFields();
        } else {
          this.updateToothFields();
          this.updateToolHiddenFields();
        }

        // DEV ONLY
        // console.log('ref module', this.formTooth.moduleNormal);
        // console.log('gen module', this.formGeneratingRack.moduleNormal);
        // console.log('ref PA', this.formTooth.pressureAngleNormal);
        // console.log('gen PA', this.formGeneratingRack.pressureAngleNormal);

        // max tip radius
        if (this.isGeneratingRack) {
          this.updateRackTipMax();
        }
      },
      updateRackTipMax() {
        // Compute max tip radius for rack generation, and assign to form field when applicable.

        const maxTipRadius = hobMaxTipRadius(this.formGeneratingRack, this.$options.units.angle);
        const maxTipRadiusCoefficient = maxTipRadius / this.formGeneratingRack.moduleNormal;

        // assign max tip radius
        if (this.formGeneratingRack.tipRadiusMax) {
            this.formGeneratingRack.tipRadiusCoefficient = maxTipRadiusCoefficient;
        }

        // notify user of limit
        if (this.formGeneratingRack.tipRadiusCoefficient > maxTipRadiusCoefficient) {
          this.$message({
            type: 'error',
            message: 'Maximum allowable tip radius coefficient is ' + maxTipRadiusCoefficient,
            center: true,
          })
        }
      },
      async changeToothGeom(valid) {
        // arg: boolean to indicate if all tooth geometry fields are valid.
        // IMPORTANT: For UX, updateGeomFields should be called before this.

        await this.changeToolGeom(valid);
      },
      async changeToolGeom(valid) {
        // arg: boolean to indicate if all tool geometry fields are valid.
        // IMPORTANT: For UX, updateGeomFields should be called before this.
        // IMPROVE: Improve robustness by also validating finishing fields before calling updateTwo.

        // update graphics
        if (valid) {
          await this.updatePixi();
        } else {
          this.resetPixi();
        }
      },

      // FINISHING
      changeFinish() {
        // @change event triggers only if the input has changed when the box loses focus or the Enter key is pressed

        // try updating geometry
        if (!this.cutWorkflow) {
          this.updateFinishStockNormal();
        } else {
          this.updateToothThicknessNormalFinish();
        }

        // validate finishing form
        let validArr = [];
        const fields = ['toothThicknessNormalFinish', 'finishStockNormal'];
        this.$refs.formFinish.validateField(fields, (eMsg) => {
          const validField = eMsg === '';
          validArr.push(validField);
        });

        // update graphics
        if (validArr.every(Boolean)) {
          this.updatePixi();
        } else {
          this.resetPixi();
        }
      },
      updateFinishStockNormal() {
        // Based on finished tooth thickness, compute the finishing stock.
        // Computed based on reference helix angle and tooth thicknesses.

        this.formFinish.finishStockNormal = gear.finishStockNormal1({
          helixAngle: math.angle2rad(this.formTooth.helixAngle, this.$options.units.angle),
          toothThicknessNormal: this.formTooth.toothThicknessNormal,
          toothThicknessNormalFinish: this.formFinish.toothThicknessNormalFinish,
        });
      },
      updateToothThicknessNormalFinish() {
        // Based on finishing stock, compute the finished tooth thickness.
        // Computed based on reference helix angle and tooth thicknesses.

        this.formFinish.toothThicknessNormalFinish = gear.toothThicknessNormalFinish1({
          helixAngle: math.angle2rad(this.formTooth.helixAngle, this.$options.units.angle),
          toothThicknessNormal: this.formTooth.toothThicknessNormal,
          finishStockNormal: this.formFinish.finishStockNormal,
        });
      },

      // TABLES
      updateTableGeom(computedTooth) {
        // Update geometry table based on computeTooth.tooth response of JSON strings.
        // Alternatively, tableGeom could be a computed prop based on a reactive param of computeTooth.

        computedTooth = this.$handlers.eng.parseTooth(computedTooth);
        this.$handlers.eng.convertTooth(computedTooth, undefined, this.$options.units);
        this.tableGeom = this.$handlers.eng.tableFinishedTooth(computedTooth, this.$options.units);
      },

      // VALIDATORS
      async validateInfo() {
        try {
          await this.$refs.formInfo.validate();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(e);
          throw 'Invalid info'
        }
      },
      async validateTooth() {
        try {
          await this.$refs.formTooth.validate();
        } catch (e) {
          // propagate error from formTooth component
          throw e
        }
      },
      validateFinish() {
        // BUG WORKAROUND validate() not working, so using validateField()
        const fields = Object.keys(this.formFinish);
        this.$refs.formFinish.validateField(fields, (eMsg) => {
          if (eMsg !== '') {
            // eslint-disable-next-line no-console
            console.warn(eMsg);
            throw 'Invalid finish'
          }
        });
      },
      async validateGeom() {
        try {
          await this.validateTooth();
          this.validateFinish();
        } catch (e) {
          // propagate error
          throw e
        }
      },
      async validateAll() {
        // Returns true if all forms are valid, otherwise display error message and return false.
        // Beware the number zero can cause issues that do not catch; but string zero is okay.

        try {
          await this.validateInfo();
          await this.validateGeom();
          return true;
        } catch (e) {
          this.$message({
            type: 'error',
            center: true,
            message: e,
          });
          return false;
        }
      },

      // BUTTON ACTIONS - CREATE
      async handleCreateLib() {
        // Create a new lib gear if all forms are valid.

        // validate, otherwise catch
        const valid = await this.validateAll();

        if (valid) {
          // API mutation, then close tab
          await this.mutateCreateLib();
          this.handleClose();
        }
      },
      async handleCreatePart() {
        // Create a new library gear and add as part if all forms are valid.

        // validate, otherwise catch
        const valid = await this.validateAll();

        if (valid) {
          // API mutation, then close tab
          await this.mutateCreatePart();
          this.handleClose();
        }
      },

      // BUTTON HANDLERS
      async handleAccept() {
        if (this.isEditing) {
          await this.handleUpdate();
        } else {
          await this.handleCreate();
        }
      },
      async handleCreate() {
        // Create a new gear if all forms are valid.

        if (this.lib) {
          await this.handleCreateLib();
        } else {
          await this.handleCreatePart();
        }
      },
      async handleUpdate() {
        // Update the lib gear if all forms are valid.
        // Cannot edit part properties, hence update only operates on library objects.

        const valid = await this.validateAll();

        if (valid) {
          // API mutation, then close tab
          await this.mutateUpdateLib(this.openId);
          this.handleClose();
        }
      },
      handleClose() {
        // Call close method on closable tab to play transition.
        // https://stackoverflow.com/questions/40957008/how-to-access-to-a-child-method-from-the-parent-in-vue-js/40957171
        this.$refs.tab.handleClose();
      },

      // GRAPHICS
      newPixi() {
        // Configuring visual quality:
        //  - https://www.html5gamedevs.com/topic/43110-circles-are-not-round/
        //  - autoDensity does not seem to help, and changes size of pane-2.
        //  - forceCanvas does not seem to help anything.

        this.pixi = new PIXI.Application({ view: this.$refs.pixi, transparent: true, antialias: true });

        // initialize gear and circles; circles layer above gear
        this.pixiGear = new PIXI.Graphics();
        this.pixiCircles = new PIXI.Graphics();
        this.pixi.stage.addChild(this.pixiGear);
        this.pixi.stage.addChild(this.pixiCircles);
        this.blankPixi();

        // starter event listeners for resizing renderer
        const viewer = document.getElementById('viewer');
        this.resizeListener = new ResizeListener(viewer, this.onViewerResize);
        this.onViewerResize();

        // listen for animate update
        this.pixi.ticker.add(() => {
          let err = this.rotater.value - this.pixiGear.rotation;
          let errSpeed = err / 5;
          let absErr = Math.abs(err);

          if (absErr > 1e-4) {
            this.pixiGear.rotation += errSpeed;
          }
        });

        // add wheel event to change rotater
        this.$refs.pixi.addEventListener('wheel', this.rotaterWheelCallback);
      },
      fitPixi(boundingRadius, refit=false) {
        // Centers and scales the pixi gear to provide a consistent view regardless of gear size.
        // Call BEFORE gear is drawn since its scale property is required for scaling linewidth.
        // PLACEHOLDER {performance} consider scaling view instead of each graphic; may require scale arg in gear fcns

        const view = this.pixi.view;

        let x, y, scale;
        if (typeof boundingRadius === 'undefined') {
          // scale to display a factor of tooth depth
          const tipRadius = this.formTooth.tipDiameter / 2;
          const rootRadius = this.formTooth.rootDiameter / 2;
          const wholeToothDepth = Math.abs(tipRadius - rootRadius);
          const marginTop = 30;  // tuned

          const outerRadius = this.internal ? rootRadius : tipRadius;
          scale = (view.clientHeight - marginTop) / (1.4 * wholeToothDepth);
          x = view.clientWidth / 2;  // horizontally center
          y = outerRadius * scale + marginTop;

          // DEV ONLY - zoom-to-fit
          // const zoomDiameter = this.internal ? this.formTooth.rimDiameter : this.formTooth.tipDiameter;
          // scale = view.clientHeight / zoomDiameter;
          // y = view.clientHeight / 2;
        } else {
          const marginTop = view.clientHeight / 2;
          scale = (view.clientHeight - marginTop) / (boundingRadius / 4);  // fit top 1/4 of circle into view
          x = view.clientWidth / 2;  // horizontally center
          y = boundingRadius * scale + marginTop;
        }

        // if refitting, maintain existing sign of scale
        const gearScale = refit && this.pixiGear.scale.x < 0 ? -scale : scale;

        // apply transformations
        this.pixiGear.position.set(x, y);
        this.pixiGear.scale.set(gearScale, gearScale);

        this.pixiCircles.position.set(x, y);
        this.pixiCircles.scale.set(scale, scale);
      },
      blankPixi() {
        const r = 100;
        this.fitPixi(r);

        if (this.internal) {
          pixi.gearInternalBlank(this.pixiGear, r);
        } else {
          pixi.gearExternalBlank(this.pixiGear, r);
        }
      },
      resetPixi() {
        this.blankPixi();
        this.pixiCircles.clear();
        this.legend = null;
      },
      async updatePixi(finishedRoot=null) {
        // Try updating 2D gear graphics.
        // Recommended to validate form data prior to calling this.

        // NOTES ABOUT REFERENCE VS GENERATED TOOTH:
        //  1. Technically the reference tooth is drawn, but it has been verified against the generated tooth.
        //  2. Circles drawn are per reference tooth, but only theoretical pitch circle differs.  Base circle is same.

        try {
          // pre-condition geom data; converts angular units to radians
          const geom = this.mapForms2Geom(true);
          const unitAngle = 'rad';

          // fetch root fillet curve
          if (!finishedRoot) {
            finishedRoot = await this.queryFinishedRoot(geom);
          }
          this.finishedRoot = finishedRoot;

          // compute slider rotation params
          this.setRotater(geom.numberOfTeeth);

          // compute graphics fitting
          this.fitPixi();

          // draw gear graphics
          pixi.gearGraphics(this.pixiGear, geom, finishedRoot, unitAngle, this.$options.drawTeeth);

          // draw circles and define legend
          pixi.gearCircles(this.pixiCircles, geom, finishedRoot, unitAngle);
          this.legend = {...pixi.GEAR_LEGEND};
          if (finishedRoot.filletBoundaryDiameter === finishedRoot.protuberanceBoundaryDiameter) {
            delete this.legend.filletBoundaryDiameter;
          }

          // track renderings
          this.renderPixi = false;
          this.renderGeneratingDiagram = true;

          // draw generating diagram
          if (this.showGeneratingDiagram) {
            this.updateGeneratingDiagram();
            this.renderPixi = true;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(e.message);
          this.resetPixi();
          this.$refs.generatingDiagram.clearPlot();
        }
      },
      setRotater(numberOfTeeth) {
        // Calculate value of rotation and set slider range for 2 tooth passes.

        numberOfTeeth = Math.abs(numberOfTeeth);
        const pitchAngleTransverse = gear.pitchAngleTransverse1(numberOfTeeth);  // negative for internal gear
        const rotaterFraction = (this.rotater.value - this.rotater.min) / (this.rotater.max - this.rotater.min);
        this.rotater.min = this.$options.rotationOffset - pitchAngleTransverse;
        this.rotater.max = this.rotater.min + 2 * pitchAngleTransverse;

        // set current value of rotation
        this.rotater.value = this.rotater.min + rotaterFraction * (this.rotater.max - this.rotater.min);
        this.pixiGear.rotation = this.rotater.value;

        // DEV ONLY
        // this.rotater.min = 0;
        // this.rotater.max = 0.0000001;
        // this.rotater.value = 0;
      },
      updateGeneratingDiagram() {
        switch (this.formTooth.cutType) {
          case this.$requests.gear.CutType.GEN_RACK:
            this.$refs.generatingDiagram.updatePlotRack();
            break;
          case this.$requests.gear.CutType.GEN_PINION:
            this.$refs.generatingDiagram.updatePlotPinion();
            break;
        }
        this.renderGeneratingDiagram = false;
      },
      toggleGeneratingDiagram() {
        this.showGeneratingDiagram = !this.showGeneratingDiagram;
        if (this.showGeneratingDiagram) {
          if (this.renderGeneratingDiagram) this.updateGeneratingDiagram();
        } else {
          this.$nextTick(() => {
            if (this.renderPixi) this.updatePixi(this.finishedRoot);
          });
        }
      },

      // EVENTS
      handleTabOpened() {
        // Called each time a tab is opened.
        // Had issues displaying graphics on tab open when this code was in mounted.

        if (!this.pixi) this.newPixi();

        // initialize geometry; helpful for beginners
        if (!this.isEditing) {
          this.updateGeomFields();
          this.updatePixi();
        }
      },
      handleTabActive() {
        // Call viewer resize callback to prevent loss of pixi view if viewer size is changed while not on this tab.
        this.onViewerResize();
        this.updatePixi(this.finishedRoot);
      },
      onViewerResize() {
        // https://github.com/pixijs/pixi.js/wiki/v4-Tips,-Tricks,-and-Pitfalls#resizing-renderer
        // ==> Suggests to use 'screen' property in repositioning and scaling.  I briefly tried unsuccessfully.

        // resize the renderer
        const parent = this.pixi.view.parentNode;
        this.pixi.renderer.resize(parent.clientWidth, parent.clientHeight);

        // resize graphics
        this.fitPixi(undefined, true);
      },
      rotaterWheelCallback(e) {
        let deltaStep = pixi.normalizeDeltaY(e);
        this.rotater.value += this.rotaterStepSize * deltaStep;
      },
    },
    beforeDestroy() {
      // stop ticker and destroy pixi
      this.pixi.stop();
      this.pixi.destroy(false, {children: true});

      // stop wheel listeners
      this.$refs.pixi.removeEventListener('wheel', this.rotaterWheelCallback);

      // remove resize listener and observer
      this.resizeListener.destroy();
    }
  }
</script>

<style scoped src="../../assets/css/form.css"/>
<style scoped lang="scss" src="../../assets/css/layout-tab-pane-1-2.scss"/>
<style scoped lang="scss" src="../../assets/css/viewer-tab.scss"/>
<style scoped lang="scss">
    $padding: 10pt;
    .pb-cushion {
        padding-bottom: $padding;
    }

    /* GRAPHICS */
    #pane-2-note {
        font-size: .7em;
        position: absolute;
        top: 10pt;
        right: 10pt;
    }
    #pane-2-btns {
        position: absolute;
        top: 7pt;
        left: 10pt;
    }
    #table-legend {
        line-height: 1.0;
    }
    #table-legend > tr td:first-child {
        width: .2em;
    }
    #table-legend > tr td:last-child {
        padding-left: .2em;
    }
    .btn-two {
        font-size: var(--icon-med-size);
        padding: 0;
        margin-left: 0;
        margin-right: 10px;
        background-color: white;
    }
    #slider-rotation {
        position: absolute;
        bottom: -19px;
        left: 10px;
        width: calc(100% - 20px);
    }
</style>
